import React, { useEffect, useMemo, useState, useCallback, useContext } from "react";
import Joi from 'joi';
import { useSnackbar } from 'notistack';
import { API } from "aws-amplify";
import { AmplifyAuthContext } from "../../App";
import moment, { now } from "moment-timezone";
import { zonedTimeToUtc } from 'date-fns-tz';
import DatePicker, { DateObject } from "react-multi-date-picker";
import {
  Grid, Paper, Typography, Button, TextField, Select, MenuItem,
  FormControl, Dialog, DialogContent, IconButton,
  Divider, InputAdornment, CircularProgress,
} from "@material-ui/core";
import { Auth } from "aws-amplify";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import ReactTable from "react-table";
import DropdownWithSearch from '../helpercomponent/StaticDropDownSelectorWithSearch'
import LicensePlateSearch from '../helpercomponent/LicensePlateSearch';
import ConfirmationModal from '../helpercomponent/confirmationModal';
import { getAPIName } from "../../config";
import handleApiErrors from "../../common/handleApiErrors";
import { getCurrentUser } from "../../common/helper";
import { getTollTypes } from './AddTolls';
import {
  faFileArrowDown,
  faPaperclip,
  faCircleInfo
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AddAuthorityModal } from "../Forms/AddAuthorityModal";

const useStyles = makeStyles(theme => ({
  inputRoot: { fontSize: 13 },
  inputLabel: { fontSize: 12 },
  noDensePadding: { paddingTop: '5px !important', paddingLeft: '3px !important' },
  root: { padding: theme.spacing(2) },
  section: { marginBottom: theme.spacing(3) },
  tilesContainer: { display: 'flex', justifyContent: 'space-between', gap: theme.spacing(2), marginTop: theme.spacing(2) },
  tile: { flex: 1, minWidth: 120, height: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', transition: 'all 0.2s ease' },
  controls: { display: "flex", gap: theme.spacing(2), alignItems: "center", justifyContent: "flex-start", marginBottom: theme.spacing(1) },
  datePickerInput: {
    textAlign: 'center', backgroundColor: '#f5f5f5', height: 36, border: '1px solid #ddd',
    borderRadius: 6, padding: '0 12px', fontSize: 14, fontWeight: 500, minWidth: 220, cursor: 'pointer',
    '&:hover': { borderColor: '#1976d2' }
  },
  tableWrapper: { marginTop: theme.spacing(1) },
  pdfViewer: { width: "100%", height: "80vh", border: "1px solid #ccc" },
  inputFrame: {
    width: '100%',
    border: '1px solid #dfe3e8',
    borderRadius: 8,
    padding: '4px 5px',
    background: '#f9fafb',
    transition: 'border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
    boxSizing: 'border-box',
    '&:hover': { borderColor: '#c5ccd4', background: '#f6f8fa' },
    '&:focus-within': { borderColor: '#2563eb', boxShadow: '0 0 0 1px rgba(37, 99, 235, 0.08), 0 6px 18px rgba(15, 23, 42, 0.08)', background: '#ffffff' }
  },
  fieldRow: { display: 'flex', flexWrap: 'wrap', gap: '10px 32px', marginBottom: 18, paddingLeft: 10 },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 220, flex: '1 1 220px', maxWidth: 260 },
  lineItemFieldGroup: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 220, flex: '1 1 220px', maxWidth: 260 },
  lineItemGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 14 },
  fieldLabel: { fontSize: 12, color: '#6b7280', fontWeight: 600, letterSpacing: '0.01em' },
  fieldGood: { border: "2px solid #2e7d32", borderRadius: 6, boxSizing: 'border-box' },
  fieldBad: { border: "2px solid #c62828", borderRadius: 6, boxSizing: 'border-box' },
  fieldWarn: { border: "2px solid #ffb300", borderRadius: 6, boxSizing: 'border-box' },
  fieldError: { border: "2px solid #d32f2f", borderRadius: 6, boxSizing: 'border-box' },
  dialogHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: theme.spacing(1) },
  disabled: { opacity: 0.5, pointerEvents: "none" }
}));

type Violation = any;
type EditableLineItem = {
  violation_item_id?: number; // Optional for new items
  temp_id?: string; // Temporary ID for new items
  isNew?: boolean; // Flag to identify new items
  notice_number?: string; violation_number?: string;
  violation_type?: number | null; violation_location?: string; violation_time?: string | null; violation_amount?: string;
  status?: string | null; violation_fees?: string; errors?: Record<string, string | undefined>;
};
type EditableDetail = {
  detail_id: number; violation_field: string; violation_field_type: string; value: string;
  confidence?: number; error?: boolean; errorMessage?: string; scope?: string; item_id?: number | null;
};
type EditState = {
  violation_id: number; status: string;
  ticket_fields: {
    notice_number?: string; license_plate?: string; state?: string; total_amount_due?: string;
    fees?: string; agency_id?: number; agency_name?: string; dealership_name?: string;
    license_verified?: boolean; agency_timezone?: string; notes?: string; errors?: Record<string, string | undefined>;
    updated_by?: string;
  };
  line_items: EditableLineItem[]; details: EditableDetail[];
};

const fieldLabelMap: Record<string, string> = {
  violation_number: 'Violation Number', violation_type: 'Type', violation_time: 'Violation Time',
  violation_location: 'Location', violation_amount: 'Amount', notice_number: 'Notice Number',
  agency_id: 'Authority', fees: 'Fees', violation_fees: 'Fees', license_plate: 'License Plate', total_amount_due: 'Total Amount',
};

// Simple date formatting - show exact values without timezone conversion
const toDateTimeLocal = (v?: string) => {
  if (!v) return "";
  return v.slice(0, 16);
};

export const violationStatuses: Record<string, string> = {
  "created": "Created", "ingest_ok": "Ingest Ok", "needs_review": "Needs Review",
  "approved": "Approved", "unprocessible": "Unprocessible", "paid": "Paid",
  "manually_solved": "Manually Solved",
};
const lineItemFields: Array<[string, string]> = [
  ["Violation Number", "violation_number"], ["Type", "violation_type"], ["Violation Time", "violation_time"],
  ["Location", "violation_location"], ["Amount", "violation_amount"], ["Fees", "violation_fees"], ["Status", "status"]
];
const analyticsTiles: Array<{ key: string | null; label: string; countKey: keyof { total: number; created: number; needsReview: number; approved: number; paid: number; unprocessible: number; manuallySolved: number } }> = [
  { key: null, label: "Total", countKey: "total" },
  { key: "created", label: "Created", countKey: "created" },
  { key: "needs_review", label: "Needs Review", countKey: "needsReview" },
  { key: "approved", label: "Approved", countKey: "approved" },
  { key: "paid", label: "Paid", countKey: "paid" },
  { key: "unprocessible", label: "Unprocessible", countKey: "unprocessible" },
  { key: "manually_solved", label: "Manually Solved", countKey: "manuallySolved" },
];

const lineItemSchema = Joi.object({
  status: Joi.string().optional().allow(null, ''),
  violation_item_id: Joi.number().integer().optional(),
  violation_number: Joi.when('status', {
    is: 'void',
    then: Joi.any().optional(),
    otherwise: Joi.string().required()
  }),
  violation_type: Joi.when('status', {
    is: 'void',
    then: Joi.any().optional(),
    otherwise: Joi.number().required()
  }),
  violation_time: Joi.when('status', {
    is: 'void',
    then: Joi.any().optional(),
    otherwise: Joi.string().required()
  }),
  utc_violation_time: Joi.string().optional().allow(null, ''),
  violation_location: Joi.when('status', {
    is: 'void',
    then: Joi.any().optional(),
    otherwise: Joi.string().required()
  }),
  violation_amount: Joi.when('status', {
    is: 'void',
    then: Joi.any().optional(),
    otherwise: Joi.number().required()
  }),
  violation_fees: Joi.when('status', {
    is: 'void',
    then: Joi.any().optional(),
    otherwise: Joi.number().required()
  })
}).unknown();

export const validateManualViolation = Joi.object({
  violation_id: Joi.number().required(), ticket_id: Joi.any().required(), license_plate: Joi.string().required(),
  toll_status_id: Joi.number().optional().allow(null),
  agency_id: Joi.number().required(), total_amount_due: Joi.number().required(), fees: Joi.number().required(),
  state: Joi.string().optional(), status: Joi.string().required(), notice_number: Joi.string().allow(null, '').optional(),
  notes: Joi.string().allow(null, '').optional(),
  updated_by: Joi.string().optional(),
  line_items: Joi.array().items(lineItemSchema).min(1).required().custom((value, helpers) => {
    // Check for duplicate violation_numbers within line_items (exclude void items)
    const violationNumbers = value
      .filter((item: any) => item.status !== 'void')
      .map((item: any) => item.violation_number)
      .filter((num: string) => num && num.trim());
    const duplicates = violationNumbers.filter((num: string, index: number) => violationNumbers.indexOf(num) !== index);
    if (duplicates.length > 0) {
      return helpers.error('any.custom', { message: `Duplicate violation numbers found: ${Array.from(new Set(duplicates)).join(', ')}` });
    }
    return value;
  })
}).options({ abortEarly: false });

const mapViolationDetails = (details: any[] = []): EditableDetail[] =>
  details.map((d: any) => ({
    detail_id: d.id, violation_field: d.violation_field, violation_field_type: d.violation_field_type,
    value: d.value ?? "", confidence: d.confidence, scope: d.scope ?? (d.violation_item_id ? "line_item" : "ticket"),
    item_id: d.violation_item_id ?? null, error: false, errorMessage: undefined
  }));

const mapViolationLineItems = (items: any[] = []): EditableLineItem[] =>
  items.map((item: any) => ({
    violation_item_id: item.id, violation_number: item.violation_number ?? "",
    violation_type: item.violation_type ?? null, violation_location: item.violation_location ?? "",
    violation_time: item.violation_time ?? item.violation_time_extracted ?? null,
    violation_amount: item.total_amount ?? item.violation_amount ?? "0", violation_fees: item.violation_fees ?? item.fees ?? "0",
    status: item.status ?? "", notice_number: item.notice_number ?? "", errors: {}
  }));

const buildEditStateFromViolation = (v: any, prevState?: EditState | null): EditState => {
  return {
    violation_id: v.id, status: v.status,
    ticket_fields: {
      notice_number: v.notice_number ?? null, license_plate: v.license_plate ?? "", state: v.state ?? "",
      total_amount_due: v.total_amount_due != null ? String(v.total_amount_due) : "",
      agency_id: v.agency_id ?? prevState?.ticket_fields.agency_id ?? undefined, 
      agency_name: v.agency_name ?? prevState?.ticket_fields.agency_name ?? "", 
      dealership_name: v.dealership_name ?? "",
      license_verified: (v as any).license_verified ?? v.status === "approved", 
      agency_timezone: v.agency_timezone ?? prevState?.ticket_fields.agency_timezone ?? "",
      fees: v.fees != null ? String(v.fees) : "", notes: v.notes ?? "", errors: {}
    },
    line_items: mapViolationLineItems(v.violation_items), 
    details: mapViolationDetails(v.violation_details)
  };
};

const ErrorText: React.FC<{ message?: string }> = ({ message }) =>
  message ? <Typography variant="caption" style={{ color: '#d32f2f', marginTop: 6 }}>{message}</Typography> : null;

const ViolationPage: React.FC = () => {
  const classes = useStyles();
  const baseInputStyle = { fontSize: 14, fontWeight: 500, paddingTop: 0 } as const;
  const inputClasses = { inputMarginDense: classes.noDensePadding };
  const { enqueueSnackbar } = useSnackbar();
  const currentAuthenticatedUser: any = useContext(AmplifyAuthContext);
  const [tollTypeOptions,setTollTypeOptions] = React.useState([])
  // Extract user role and dealership info
  const userRole = currentAuthenticatedUser?.attributes?.given_name ?? 'Individual';
  const dealershipId = currentAuthenticatedUser?.attributes?.family_name ?? null;
  const isAdmin = userRole === 'Admin';
  const isGroupUser = userRole === 'Group';
  const dealershipGroupId = isGroupUser ? dealershipId : null;
  // Check if user is properly authenticated
  const isUserLoaded = currentAuthenticatedUser?.attributes != null;

  // State
  const [analyticsRange, setAnalyticsRange] = useState({ from: moment().subtract(30, "days").startOf("day").toISOString(), to: moment().endOf("day").toISOString() });
  const [analytics, setAnalytics] = useState({ total: 0, needsReview: 0, paid: 0, approved: 0, created: 0, unprocessible: 0, manuallySolved: 0 });
  const [selectedStatusTile, setSelectedStatusTile] = useState<string | null>("needs_review");
  const [allViolations, setAllViolations] = useState<Violation[]>([]); // All violations within date range
  const [violations, setViolations] = useState<Violation[]>([]); // Filtered by status for table
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedViolation, setSelectedViolation] = useState<Violation | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showMarkPaidConfirmation, setShowMarkPaidConfirmation] = useState(false);
  const [isSubmittingMarkPaid, setIsSubmittingMarkPaid] = useState(false);
  const [violationStatusChange, setViolationStatusChange] = useState<string | null>(null);
  const [isVoiding, setIsVoiding] = useState(false);
  const [touchedViolationNumbers, setTouchedViolationNumbers] = useState<Set<number | string>>(new Set());
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);
  const [authorityForm, setAuthorityForm] = useState({ name: '', agency_timezone: '', description: '' });


  // API Calls - Fetch all violations within date range
  const fetchViolationsByDateRange = useCallback(async () => {
    //Only fetch if user is properly loaded
    if (!isUserLoaded) return;
    setIsTableLoading(true);
    setAllViolations([]);
    setViolations([]);
    try {
      const apiName = getAPIName();
      let apiUrl = `/api/v1/violations?gte-inserted_timestamp=${analyticsRange.from}&lt-inserted_timestamp=${analyticsRange.to}`;
      // Add dealership filtering for non-admin users
      if (!isAdmin) {
        if (isGroupUser && dealershipGroupId) {
          apiUrl += `&dealership.dealershipgroupid=${dealershipGroupId}`;
        } else if (!isGroupUser && dealershipId) {
          apiUrl += `&vehicle.dealershipid=${dealershipId}`;
        }
      }
      
      const res = await API.get(apiName, apiUrl, {});
      const list = res?.body || res || [];
      setAllViolations(list);
      setAnalytics({
        total: list.length,
        needsReview: list.filter((v: Violation) => v.status === "needs_review").length,
        paid: list.filter((v: Violation) => v.status === "paid").length,
        approved: list.filter((v: Violation) => v.status === "approved").length,
        created: list.filter((v: Violation) => v.status === "created").length,
        unprocessible: list.filter((v: Violation) => v.status === "unprocessible").length,
        manuallySolved: list.filter((v: Violation) => v.status === "manually_solved").length
      });
    } finally {
      setIsTableLoading(false);
    }
  }, [analyticsRange, isAdmin, isGroupUser, dealershipGroupId, dealershipId, isUserLoaded]);

  // Filter violations by selected status for table display
  useEffect(() => {
    if (selectedStatusTile === null) {
      setViolations(allViolations);
    } else {
      setViolations(allViolations.filter((v: Violation) => v.status === selectedStatusTile));
    }
  }, [allViolations, selectedStatusTile]);
  
  // Fetch violations when date range changes or when user is loaded
  useEffect(() => {
    if (!isUserLoaded) return;
    fetchViolationsByDateRange();
  }, [fetchViolationsByDateRange, isUserLoaded]);
  
  // Fetch current user details
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const user = await getCurrentUser();
      setCurrentUsername(user.username);
    };
    fetchCurrentUser();
  }, []);
  
  useEffect(() => {
    if (!open || !selectedViolation?.id) return;
    const refreshViolation = async () => {
      try {
        const res = await API.get(getAPIName(), `/api/v1/violations?id=${selectedViolation.id}`, {});
        const latest = res?.body?.[0] || res?.[0];
        if (latest) {
          setSelectedViolation(latest); 
          setEditState(prev => buildEditStateFromViolation(latest, prev));
          setIsVerified(latest.status === 'approved');
        }
      } catch (e) { console.error('Failed to refresh violation', e); }
    };
    refreshViolation();
  }, [open, selectedViolation?.id]);

  useEffect(() => { getTollTypes((setTollTypeOptions)) }, []);
  
  // Helpers
  const handleDialogClose = () => {
    setOpen(false);
  };

  const getDetailConfidence = (scope: 'ticket' | 'line_item', fieldKeys: string[], itemId?: number) => {
    if (!editState) return undefined;
    const d = editState.details.find(det => det.scope === scope && (scope !== 'line_item' || det.item_id === itemId) && fieldKeys.includes((det.violation_field || '').toLowerCase()));
    return d?.confidence;
  };

  const fieldClassFrom = (confidence: number | undefined | null, error?: boolean) => {
    if (error) return classes.fieldError;
    if (isVerified) return undefined; // No confidence borders after verification
    if (confidence == null) return undefined;
    if (confidence < 75) return classes.fieldBad;
    if (confidence < 90) return classes.fieldWarn;
    return classes.fieldGood;
  };

  const isTicketFieldDisabled = (fieldName: string) => isReadOnly || (isVerified && ['license_plate'].includes(fieldName));
  const isLineItemFieldDisabled = (fieldName: string, lineItem?: EditableLineItem) => {
    if (isReadOnly) return true;
    // If item is void, disable all fields except status itself
    if (lineItem?.status === 'void' && fieldName !== 'status') return true;
    return false;
  }; 
  const updateTicketField = (field: keyof EditState['ticket_fields'], value: string) => {
    setEditState(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ticket_fields: { ...prev.ticket_fields, [field]: value, errors: { ...prev.ticket_fields.errors, [field]: undefined } } };
      if (field === 'notice_number') {
        updated.line_items = prev.line_items.map(li => {
          // Check if this item's violation_number has been touched (by id or temp_id)
          const isTouched = (li.violation_item_id && touchedViolationNumbers.has(li.violation_item_id)) || 
                           (li.temp_id && touchedViolationNumbers.has(li.temp_id));
          return isTouched ? li : { ...li, violation_number: value };
        });
      }
      return updated;
    });
  };

  const updateLineItem = (itemId: number | string | undefined, field: string, value: string | null) => {
    setEditState(prev => prev ? { 
      ...prev, 
      line_items: prev.line_items.map(li => {
        // Match by violation_item_id for existing items, or temp_id for new items
        const isMatch = li.violation_item_id === itemId || li.temp_id === itemId;
        if (isMatch) {
          // If changing status to void, clear all errors
          if (field === 'status' && value === 'void') {
            return { ...li, [field]: value, errors: {} };
          }
          return { ...li, [field]: value, errors: { ...li.errors, [field]: undefined } };
        }
        return li;
      }) 
    } : prev);
  };

  const setLineItemError = (itemId: number | string | undefined, field: string, error: string | undefined) => {
    setEditState(prev => prev ? { 
      ...prev, 
      line_items: prev.line_items.map(li => {
        const isMatch = li.violation_item_id === itemId || li.temp_id === itemId;
        return isMatch ? { ...li, errors: { ...li.errors, [field]: error } } : li;
      }) 
    } : prev);
  };

  const setTicketError = (k: string, label: string) => setEditState(p => p ? { ...p, ticket_fields: { ...p.ticket_fields, errors: { ...p.ticket_fields.errors, [k]: `${label} is required` } } } : p);

  const openDialog = (v: Violation) => {
    // Reset state when opening a different violation
    if (!selectedViolation || selectedViolation.id !== v.id) {
      setSelectedViolation(v);
      setEditState(buildEditStateFromViolation(v));
      setIsVerified(v.status === 'approved');
      setTouchedViolationNumbers(new Set());
    }
    setOpen(true);
  };

  const handleVehicleSelect = (data: { licenseplate: string; state: string; dealershipname: string }) => {
    if (!editState) return;
    setEditState(prev => prev ? { ...prev, ticket_fields: { ...prev.ticket_fields, license_plate: data.licenseplate, state: data.state, dealership_name: data.dealershipname || prev.ticket_fields.dealership_name, license_verified: true, errors: { ...prev.ticket_fields.errors, license_plate: undefined } } } : prev);
    enqueueSnackbar('License plate verified and populated', { variant: 'success' });
  };

  const handleVehicleClear = () => {
    if (!editState) return;
    // Mark as unverified and clear state/dealership
    setEditState(prev => prev ? { ...prev, ticket_fields: { ...prev.ticket_fields, state: '', dealership_name: '', license_verified: false } } : prev);
  };

  
  const markViolationNumberTouched = (itemId: number | string) => {
    setTouchedViolationNumbers(prev => new Set(prev).add(itemId));
  };

  // Add a new empty line item
  const addNewLineItem = () => {
    // Find the default toll type (you can adjust this logic)
    const defaultTollType = tollTypeOptions.find((o: any) => o.code === 'toll');
    const newItem: EditableLineItem = {
      temp_id: `new-${Date.now()+ Math.random().toString(36).substr(2)}`, // Temporary ID for React key and backend reconciliation
      isNew: true,
      violation_number: editState?.ticket_fields.notice_number || "",
      violation_type: defaultTollType ? (defaultTollType as any).id : null,
      violation_time: null,
      violation_location: "",
      violation_amount: "0",
      violation_fees: "0",
      status: null,
      errors: {}
    };
    setEditState(prev => prev ? { ...prev, line_items: [...prev.line_items, newItem] } : prev);
  };

  // Remove a new line item (only allowed for new items)
  const removeNewLineItem = (temp_id: string) => {
    setEditState(prev => prev ? { ...prev, line_items: prev.line_items.filter(li => li.temp_id !== temp_id) } : prev);
  };

  // Check if there's only one line item total (considering new items too)
  const isSingleItem = editState?.line_items.length === 1;
  // Non-admin users cannot edit; admin users can only edit needs_review and approved statuses
  const isReadOnly = !isAdmin || (editState ? !['needs_review', 'approved'].includes(editState.status) : false);

  const buildManualViolationPayload = (status: string | null) => {
    if (!editState) return null;
    // For single item, use line item amounts for ticket amounts
    const singleLineItem = isSingleItem ? editState.line_items[0] : null;
    const total_amount_due = singleLineItem ? singleLineItem.violation_amount : editState.ticket_fields.total_amount_due;
    const fees = singleLineItem ? singleLineItem.violation_fees : editState.ticket_fields.fees;
    const agencyTimezone = editState.ticket_fields.agency_timezone;
    return {
      violation_id: editState.violation_id, ticket_id: selectedViolation?.ticket_id, license_plate: editState.ticket_fields.license_plate,
      agency_id: editState.ticket_fields.agency_id, total_amount_due, fees,
      state: editState.ticket_fields.state, status, notice_number: editState.ticket_fields.notice_number,
      notes: editState.ticket_fields.notes || "",
      updated_by: currentUsername || null,
      toll_status_id: 9, // Manual Toll
      line_items: editState.line_items.map(li => {
        let utcViolationTime = li.violation_time;
        // Convert violation_time from agency timezone to UTC when marking as paid
        if (status == 'paid' && agencyTimezone && utcViolationTime) {
          try {
            utcViolationTime = agencyTimezone ? zonedTimeToUtc(utcViolationTime.endsWith('Z') ? utcViolationTime.slice(0, -1) : utcViolationTime, agencyTimezone).toISOString() : utcViolationTime;
          } catch (err) {
            console.error('Failed to convert violation_time to UTC:', err);
          }
        }
        const item: any = {
          violation_number: li.violation_number, violation_type: li.violation_type,
          violation_time: li.violation_time,
          utc_violation_time: utcViolationTime,
          violation_location: li.violation_location, violation_amount: li.violation_amount,
          violation_fees: li.violation_fees, status: li.status === "" ? null : li.status
        };
        // Only include violation_item_id for existing items
        // New items are identified by the absence of violation_item_id
        if (li.violation_item_id) {
          item.violation_item_id = li.violation_item_id;
        }
        return item;
      })
    };
  };

  // Line item field renderer - itemId can be violation_item_id or temp_id
  const renderLineItemField = (itemId: number | string | undefined, lineItem: EditableLineItem, label: string, key: string) => {
    const errorText = lineItem.errors?.[key];
    const isNewItem = lineItem.isNew;
    const common = { key: `${itemId}-${key}`, disabled: isLineItemFieldDisabled(key, lineItem) && !isNewItem, error: !!errorText };

    const frameClass = (confKeys: string[]) => `${classes.inputFrame} ${fieldClassFrom(getDetailConfidence('line_item', confKeys, lineItem.violation_item_id)) || ''}`;
    const wrapField = (children: React.ReactNode, confKeys: string[]) => (
      <div key={common.key} className={classes.lineItemFieldGroup}>
        <Typography variant="caption" color="textSecondary" className={classes.fieldLabel}>{label}</Typography>
        <div className={frameClass(confKeys)}>{children}</div>
        <ErrorText message={errorText} />
      </div>
    );

    if (key === "status") {
      // Line item status: only allow changing TO void, not from void (not applicable for new items)
      const currentValue = (lineItem as any)[key] ?? "";
      return wrapField(
        <FormControl {...common} size="small" disabled={common.disabled || isNewItem} fullWidth>
          <Select value={currentValue} onChange={e => updateLineItem(itemId, key, e.target.value === "" ? null : e.target.value as string)} displayEmpty>
            <MenuItem value="">None</MenuItem>
            <MenuItem value="void">Void</MenuItem>
          </Select>
        </FormControl>,
        ['status']
      );
    }

    if (key === "violation_type") return wrapField(
      <FormControl {...common} size="small" fullWidth>
        <Select value={(lineItem as any)[key] ?? null} onChange={e => {
          updateLineItem(itemId, key, e.target.value as any);
        }}>
          {tollTypeOptions.map((o: any) => <MenuItem key={o.id} value={o.id}>{o.description}</MenuItem>)}
        </Select>
      </FormControl>,
      ['violation_type']
    );

    if (key === 'violation_time') return wrapField(
      <TextField 
        type="datetime-local" 
        value={toDateTimeLocal((lineItem as any)[key] ?? null)} 
        variant="standard" 
        size="small" 
        fullWidth 
        {...common} 
        InputLabelProps={{ shrink: true }} 
        InputProps={{ style: baseInputStyle, classes: inputClasses }} 
        onChange={e => updateLineItem(itemId, key, e.target.value)} 
      />,
      ['violation_time', 'datetime']
    );

    if (key === 'violation_amount' || key === 'violation_fees') return wrapField(
      <TextField value={(lineItem as any)[key] ?? ''} inputProps={{ step: '0.01' }} variant="standard" size="small" type="number" fullWidth {...common} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, style: baseInputStyle, classes: inputClasses }} onChange={e => updateLineItem(itemId, key, e.target.value)} />,
      ['amount', 'amount_due']
    );

    // For violation_number, track when user focuses on it to stop auto-sync
    if (key === 'violation_number') {
      return wrapField(
        <TextField 
          value={(lineItem as any)[key] ?? ""} 
          variant="standard" 
          size="small" 
          fullWidth 
          {...common} 
          InputProps={{ style: baseInputStyle, classes: inputClasses }} 
          onFocus={() => {
            const idToTrack = lineItem.violation_item_id ?? lineItem.temp_id;
            if (idToTrack) markViolationNumberTouched(idToTrack);
          }}
          onChange={e => updateLineItem(itemId, key, e.target.value)} 
        />,
        [key]
      );
    }

    return wrapField(
      <TextField value={(lineItem as any)[key] ?? ""} variant="standard" size="small" fullWidth {...common} InputProps={{ style: baseInputStyle, classes: inputClasses }} onChange={e => updateLineItem(itemId, key, e.target.value)} />,
      [key]
    );
  };

  const handleVerify = async () => {
    if (!editState) return;
    if (!editState.ticket_fields.license_verified) return enqueueSnackbar('Verify license plate first', { variant: 'error' });
    const payload = buildManualViolationPayload(editState.status === 'unprocessible' ? 'unprocessible' : 'approved');
    if (!payload) return;
    const { error } = validateManualViolation.validate(payload, { abortEarly: false });
    if (!error) {
      const hasNewItems = editState.line_items.some(li => li.isNew);
      try {
        setIsVerifying(true);
        await API.put(getAPIName(), `/api/v1/violations/manual_violation_update`, { body: payload });
        
        // If we inserted new line items, fetch the updated violation to get violation_item_ids
        if (hasNewItems && selectedViolation?.id) {
          const res = await API.get(getAPIName(), `/api/v1/violations?id=${selectedViolation.id}`, {});
          const latest = res?.body?.[0] || res?.[0];
          if (latest) {
            setSelectedViolation(latest);
            setEditState(prev => buildEditStateFromViolation(latest, prev));
          }
        }
        
        enqueueSnackbar('Verification successful', { variant: 'success' });
        setEditState(prev => prev ? { ...prev, status: 'approved' } : prev);
        setIsVerified(true);
        // Refresh violations list and analytics after successful verification
        fetchViolationsByDateRange();
      } catch (err) { handleApiErrors(err, enqueueSnackbar); } finally { setIsVerifying(false); }
      return;
    }
    // Check for custom validation errors first (e.g., duplicate violation numbers)
    const customError = error.details.find(d => d.type === 'any.custom' && d.path[0] === 'line_items');
    if (customError) {
      const customMessage = customError.context?.message || customError.message || 'Duplicate violation numbers detected in line items';
      enqueueSnackbar(customMessage, { variant: 'error' });
      return;
    }
    
    const itemErrors: Record<number, string[]> = {}, topErrors = new Set<string>();
    error.details.forEach(d => {
      const [root, idx, field] = d.path;
      const key = String(field ?? d.context?.key ?? root), label = fieldLabelMap[key] || key;
      if (root === 'line_items' && typeof idx === 'number') {
        if (!itemErrors[idx]) itemErrors[idx] = [];
        if (!itemErrors[idx].includes(label)) itemErrors[idx].push(label);
        const lineItem = editState.line_items[idx];
        const itemId = lineItem.violation_item_id ?? lineItem.temp_id;
        setLineItemError(itemId, key, `${label} is required`);
      } else if (root) { topErrors.add(label); setTicketError(String(root), label); }
    });
    const msgs: string[] = [];
    if (topErrors.size) msgs.push(`${Array.from(topErrors).join(', ')} required`);
    Object.entries(itemErrors).forEach(([i, labels]) => msgs.push(`Item ${Number(i) + 1}: missing ${labels.join(', ')}`));
    if (msgs.length > 0) {
      enqueueSnackbar(msgs.join(' · '), { variant: 'error' });
    }
  };

  const handleMarkPaidClick = () => { 
    if (editState && isVerified) {
      
      // Validate the payload before showing confirmation
      const payload = buildManualViolationPayload('paid');
      if (!payload) return;
      
      const { error } = validateManualViolation.validate(payload, { abortEarly: false });
      if (error) {
        // Check for custom validation errors first (e.g., duplicate violation numbers)
        const customError = error.details.find(d => d.type === 'any.custom' && d.path[0] === 'line_items');
        if (customError) {
          const customMessage = customError.context?.message || customError.message || 'Duplicate violation numbers detected in line items';
          enqueueSnackbar(customMessage, { variant: 'error' });
          return;
        }
        
        const itemErrors: Record<number, string[]> = {}, topErrors = new Set<string>();
        error.details.forEach(d => {
          const [root, idx, field] = d.path;
          const key = String(field ?? d.context?.key ?? root), label = fieldLabelMap[key] || key;
          if (root === 'line_items' && typeof idx === 'number') {
            if (!itemErrors[idx]) itemErrors[idx] = [];
            if (!itemErrors[idx].includes(label)) itemErrors[idx].push(label);
            const lineItem = editState.line_items[idx];
            const itemId = lineItem.violation_item_id ?? lineItem.temp_id;
            setLineItemError(itemId, key, `${label} is required`);
          } else if (root) { topErrors.add(label); setTicketError(String(root), label); }
        });
        const msgs: string[] = [];
        if (topErrors.size) msgs.push(`${Array.from(topErrors).join(', ')} required`);
        Object.entries(itemErrors).forEach(([i, labels]) => msgs.push(`Item ${Number(i) + 1}: missing ${labels.join(', ')}`));
        if (msgs.length > 0) {
          enqueueSnackbar(msgs.join(' · '), { variant: 'error' });
        }
        return;
      }
      if (!editState.ticket_fields.notes || editState.ticket_fields.notes.trim() === '') {
        enqueueSnackbar('Notes field is required before marking as paid', { variant: 'error' });
        return;
      }
      setShowMarkPaidConfirmation(true);
    }
  };

  const handleMarkPaidConfirm = async () => {
    try {
      setIsSubmittingMarkPaid(true);
      const payload = buildManualViolationPayload('paid');
      if (!payload) return;
      console.log('Marking violation as paid with payload:', payload);
      await API.put(getAPIName(), `/api/v1/violations/violation_payment`, { body: payload });
      enqueueSnackbar('Payment marked successfully', { variant: 'success' });
      setShowMarkPaidConfirmation(false);
      handleDialogClose();
      // Refresh violations list and analytics after marking as paid
      fetchViolationsByDateRange();
    } catch (err) { handleApiErrors(err, enqueueSnackbar); } finally { setIsSubmittingMarkPaid(false); }
  };

  const handleStatusChangeConfirm = async () => {
    if (!editState || !selectedViolation || !violationStatusChange) return;
    try {
      setIsVoiding(true);
      const payload = {
        id: editState.violation_id,
        ticket_id: selectedViolation.ticket_id,
        source: selectedViolation.source || null,
        source_message_id: selectedViolation.source_message_id || null,
        msg_hash: selectedViolation.msg_hash || null,
        source_message_sender: selectedViolation.source_message_sender || null,
        source_message_subject: selectedViolation.source_message_subject || null,
        ticket_created_at: selectedViolation.ticket_created_at || null,
        ticket_updated_at: moment().toISOString(),
        status: violationStatusChange,
        notes: selectedViolation.notes || null,
        notice_number: editState.ticket_fields.notice_number == ""? null : null,
        total_amount_due: editState.ticket_fields.total_amount_due ? parseFloat(editState.ticket_fields.total_amount_due) : null,
        license_plate: editState.ticket_fields.license_plate || null,
        state: editState.ticket_fields.state || null,
        description: selectedViolation.description || null,
        agency_id: editState.ticket_fields.agency_id || null,
        fees: editState.ticket_fields.fees ? parseFloat(editState.ticket_fields.fees) : null,
        is_approved: selectedViolation.is_approved || null,
      };
      await API.put(getAPIName(), `/api/v1/violations/${editState.violation_id}`, { body: payload });
      enqueueSnackbar(`Ticket marked as ${violationStatuses[violationStatusChange] || violationStatusChange} successfully`, { variant: 'success' });
      setViolationStatusChange(null);
      handleDialogClose();
      fetchViolationsByDateRange();
    } catch (err) { handleApiErrors(err, enqueueSnackbar); } finally { setIsVoiding(false); }
  };

  const columns = useMemo(() => {
    const getZendeskTicketUrl = (ticketId: string) =>
      isAdmin
        ? `https://tollaid.zendesk.com/agent/tickets/${ticketId}`
        : `https://tollaid.zendesk.com/hc/en-us/requests`;
    return [
    {Header: "Violation #",accessor: "id"},
    { Header: "Store", Cell: ({ original }: any) => original?.vehicle?.dealership?.name || "-" },
    { Header: "Sender Mail", accessor: "source_message_sender"},
    { Header: "Ticket", accessor: "ticket_id",    
      Cell: (props: any) =>
      props.value ? (
        <a
          target="_blank"
          rel="noreferrer"
          href={getZendeskTicketUrl(props.value)}
        >
          {props.value}
        </a>
      ) : null
    }, 
    { Header: "Notice", accessor: "notice_number" },
    { Header: "Plate", accessor: "license_plate" }, 
    { Header: "Amount", accessor: "total_amount_due", Cell: ({ value }: any) => value ? `$ ${value}` : '-' },
    { Header: "Fees", accessor: "fees", Cell: ({ value }: any) => value ? `$ ${value}` : '-' },
    { Header: "Status", accessor: "status", Cell: ({ value }: any) => violationStatuses[value] || value },
    { Header: "Location", accessor: "violation_items", Cell: ({ value }: any) => value?.length > 1 ? "" : value?.[0]?.violation_location || ""},
    { Header: "Ticket Created At", accessor: "ticket_created_at", Cell: ({ value }: any) => value ? moment(value).local().format('MM/DD/YYYY h:mm a') : '-' }, 
    { Header: "Inserted Time", accessor: "inserted_timestamp", Cell: ({ value }: any) => value ? moment(value).local().format('MM/DD/YYYY h:mm a') : '-'  },
    { Header: <FontAwesomeIcon icon={faPaperclip} style={{ fontSize: 17 }}/>, accessor: "violation_attachments", 
      Cell: ({ value }: any) => {
        const pdfUri = value?.[0]?.storage_uri;
        if (!pdfUri) return null;
        return (
          <a 
            href={`https://tollaid-support.s3.us-east-2.amazonaws.com${pdfUri}`} 
            target="_blank" 
            rel="noreferrer"
            title="Open PDF"
            style={{ color: '#c62828', fontSize: 18 }}
          >
            <FontAwesomeIcon icon={faFileArrowDown} />
          </a>
        );
      }
    },
    { Header: "Action", Cell: ({ original }: any) => {
      const canEdit = isAdmin && ['needs_review', 'approved', 'unprocessible'].includes(original.status);
      const canView = ['needs_review', 'approved', 'unprocessible', 'paid','manually_solved'].includes(original.status) || !isAdmin;
      
      if (canEdit) {
        return <IconButton size="small" onClick={() => openDialog(original)} title="Edit"><EditIcon /></IconButton>;
      } else if (canView) {
        return <IconButton size="small" onClick={() => openDialog(original)} title="View Details"><FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: 18 }} /></IconButton>;
      }
      return null;
    } }
    ]
  }, [isAdmin, isUserLoaded]);
  const getTileStyle = (key: string | null) => ({ cursor: 'pointer', background: selectedStatusTile === key ? '#f5f5f5' : '#fff', border: selectedStatusTile === key ? '2px solid #1976d2' : '1px solid #ddd' });

  // Generate Zendesk ticket URL based on user role
  const getZendeskTicketUrl = (ticketId: string) => 
    isAdmin 
      ? `https://tollaid.zendesk.com/agent/tickets/${ticketId}`
      : `https://tollaid.zendesk.com/hc/en-us/requests`;

  return (
    <Paper className={classes.root}>
      {/* Analytics */}
      <div className={classes.section}>
        <div style={{display:'flex', justifyContent:'space-between'}}>
        <Typography variant="h6" style={{ textAlign: 'left' }}>Analytics</Typography>
        <div style={{ position: 'relative' }}>
          <DatePicker
            range
            numberOfMonths={2}
            format="MMM DD, YYYY"
            calendarPosition="bottom-left"
            portal
            value={[
              new DateObject(analyticsRange.from),
              new DateObject(analyticsRange.to)
            ]}
            onChange={dates =>
              Array.isArray(dates) && dates.length === 2 &&
              setAnalyticsRange({
                from: dates[0].toDate().toISOString(),
                to: moment(dates[1].toDate()).endOf('day').toISOString()
              })
            }
            inputClass={classes.datePickerInput}
          />
        </div>

        </div>
        <div className={classes.controls}>
        </div>
        <div className={classes.tilesContainer}>
          {analyticsTiles
            .filter(t => {
              // For non-admin users, only show: needs_review, approved, paid, manually_solved
              if (!isAdmin && t.key !== null && !['needs_review', 'approved', 'paid', 'manually_solved'].includes(t.key)) {
                return false;
              }
              // Hide total tile for non-admin users
              if (!isAdmin && t.key === null) {
                return false;
              }
              return true;
            })
            .map(t => (
            <div key={t.label} className={classes.tile} onClick={() => setSelectedStatusTile(t.key)} style={getTileStyle(t.key)}>
              <b>{t.label}</b>
              <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{(analytics as any)[t.countKey] || 0}</div>
            </div>
          ))}
        </div>
      </div>
      <Divider />

      {/* Table */}
      <div className={classes.section}>
        <Typography variant="h6" style={{ textAlign: 'left', marginTop: 16 }}>Violations</Typography>
        <div className={classes.tableWrapper} style={{ position: 'relative', minHeight: 300 }}>
          {isTableLoading && <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}><CircularProgress /></div>}
          <div style={{ opacity: isTableLoading ? 0.5 : 1 }}>
            <ReactTable className="-striped -highlight" data={violations} columns={columns} defaultPageSize={10} defaultSorted={[{ id: 'id', desc: true }]} /></div>
        </div>
      </div>

      {/* Dialog */}
      <Dialog fullWidth maxWidth="lg" open={open} onClose={handleDialogClose}>
        <div className={classes.dialogHeader}>
          <Typography variant="h4">Violation Details</Typography>
          <IconButton onClick={handleDialogClose}><CloseIcon /></IconButton>
        </div>
        <DialogContent>
          {(isVerifying || isSubmittingMarkPaid || isVoiding) && <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></div>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {selectedViolation?.violation_attachments?.[0]?.storage_uri
                ? selectedViolation.violation_attachments[0].content_type === 'application/pdf'
                  ? <iframe title="PDF" loading="lazy" src={`https://tollaid-support.s3.us-east-2.amazonaws.com${selectedViolation.violation_attachments[0].storage_uri}`} className={classes.pdfViewer} />
                  : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh', border: '1px solid #ccc', borderRadius: 8, background: '#f5f5f5' }}>
                      <Typography variant="body1" color="textSecondary">Attachment is not in PDF format</Typography>
                    </div>
                : <Typography>No PDF available</Typography>}
            </Grid>
            <Grid item xs={12} sm={6} style={{ maxHeight: "80vh", overflowY: "auto", paddingRight: 8, fontSize: 13, position: 'relative' }}>
              {selectedViolation && editState && (<>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, fontSize: 16 }}>Violation (Ticket) Details</Typography>
                  <div style={{ minWidth: 160, maxWidth: 180, marginRight: 12 }}>
                    {/* Violation Status Field */}
                    <Typography variant="caption" color="textSecondary" className={classes.fieldLabel}>Status</Typography>
                    <div className={classes.inputFrame}>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={editState.status ?? ''}
                          disabled={isReadOnly && editState.status !== 'unprocessible'}
                          onChange={e => {
                            const newStatus = e.target.value as string;
                            if (['unprocessible', 'manually_solved'].includes(newStatus)) {
                              setViolationStatusChange(newStatus);
                            }
                          }}
                          style={{ fontSize: 14 }}
                        >
                          <MenuItem value={editState.status}>{violationStatuses[editState.status] || editState.status}</MenuItem>
                          {editState.status !== 'unprocessible' && <MenuItem value="unprocessible">Unprocessible</MenuItem>}
                          {editState.status !== 'manually_solved' && <MenuItem value="manually_solved">Manually Solved</MenuItem>}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                </div>
                <div className={classes.fieldRow}>
                  {/* Notice Number */}
                  <div className={classes.fieldGroup}>
                    <Typography variant="caption" color="textSecondary" className={classes.fieldLabel}>Notice Number</Typography>
                    <div className={`${classes.inputFrame} ${fieldClassFrom(getDetailConfidence('ticket', ['notice_number'])) || ''}`}>
                      <TextField value={editState.ticket_fields.notice_number} onChange={e => updateTicketField('notice_number', e.target.value)} variant="standard" size="small" fullWidth disabled={isTicketFieldDisabled('notice_number')} error={!!editState.ticket_fields.errors?.notice_number} InputProps={{ style: baseInputStyle, classes: inputClasses }} />
                    </div>
                    <ErrorText message={editState.ticket_fields.errors?.notice_number} />
                  </div>
                  {/* Ticket ID (link to Zendesk) */}
                  <div className={classes.fieldGroup}>
                    <Typography variant="caption" color="textSecondary"  className={classes.fieldLabel}>Ticket ID</Typography>
                    <div style={{ marginTop: 8 }}>
                      {selectedViolation.ticket_id ? (
                        <a
                          href={getZendeskTicketUrl(selectedViolation.ticket_id)}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 14, fontWeight: 500, color: '#1976d2', textDecoration: 'none' }}
                        >
                          {selectedViolation.ticket_id}
                        </a>
                      ) : (
                        <Typography variant="body2" style={{ fontSize: 14, color: '#666' }}>-</Typography>
                      )}
                    </div>
                  </div>
                  {/* Total Amount - only show for multiple items */}
                  {!isSingleItem && (
                    <div className={classes.fieldGroup}>
                      <Typography variant="caption" color="textSecondary" className={classes.fieldLabel}>Total Amount</Typography>
                      <div className={`${classes.inputFrame} ${fieldClassFrom(getDetailConfidence('ticket', ['amount', 'amount_due'])) || ''}`}>
                        <TextField disabled={isReadOnly} type="number" inputProps={{ step: '0.01' }} value={editState.ticket_fields.total_amount_due ?? ''} onChange={e => updateTicketField('total_amount_due', e.target.value)} error={!!editState.ticket_fields.errors?.total_amount_due} variant="standard" size="small" fullWidth InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, style: baseInputStyle, classes: inputClasses }} />
                      </div>
                      <ErrorText message={editState.ticket_fields.errors?.total_amount_due} />
                    </div>
                  )}
                  {/* Fees - only show for multiple items */}
                  {!isSingleItem && (
                    <div className={classes.fieldGroup}>
                      <Typography variant="caption" color="textSecondary" className={classes.fieldLabel}>Total Fees</Typography>
                      <div className={classes.inputFrame}>
                        <TextField disabled={isReadOnly} type="number" inputProps={{ step: '0.01' }} value={editState.ticket_fields.fees ?? ''} onChange={e => updateTicketField('fees', e.target.value)} variant="standard" size="small" error={!!editState.ticket_fields.errors?.fees} fullWidth InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, style: baseInputStyle, classes: inputClasses }} />
                      </div>
                      <ErrorText message={editState.ticket_fields.errors?.fees} />
                    </div>
                  )}
                  {/* License Plate Search */}
                  <div className={classes.fieldGroup}>
                    <Typography variant="caption" color="textSecondary" className={classes.fieldLabel}>Select Vehicle</Typography>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <LicensePlateSearch initialLicensePlate={selectedViolation.license_plate || ''} onSelect={handleVehicleSelect} onClear={handleVehicleClear} disabled={isTicketFieldDisabled('license_plate')} error={!!editState.ticket_fields.errors?.license_plate} helperText={editState.ticket_fields.errors?.license_plate} />
                      <span>{editState.ticket_fields.license_verified && <Typography variant="caption" style={{ color: '#2e7d32', fontWeight: 500, marginRight:'5px' }}>✓</Typography>}
                      <Typography variant="caption" color="textSecondary">
                        Location: {editState.ticket_fields.dealership_name || '-'}
                      </Typography>
                      </span>
                    </div>
                  </div>
                  {/* Agency dropdown */}
                  <div className={classes.fieldGroup}>
                    <Typography variant="caption" color="textSecondary" className={classes.fieldLabel}>Authority</Typography>
                    <div style={{ opacity: isTicketFieldDisabled('agency_id') ? 0.5 : 1, pointerEvents: isTicketFieldDisabled('agency_id') ? 'none' : 'auto' }}>
                      <DropdownWithSearch 
                        options_path="/tollagencies" 
                        key_selector="id" 
                        addOption={{
                          label: '+ Add Authority',
                        }}
                        addModal={({ open, onClose, onSuccess }) => (
                          <AddAuthorityModal
                            open={open}
                            onClose={onClose}
                            form={authorityForm}
                            setForm={setAuthorityForm}
                            onSuccess={() => {
                              onSuccess();     
                            }}
                          />
                        )}                        
                        value_selector="name" 
                        additional_selector_value="description" 
                        value={editState.ticket_fields.agency_id}
                        onChange={(opt: any) => {
                          const data = opt?.data;
                          setEditState(prev => {const updated = prev ? { ...prev, ticket_fields: { ...prev.ticket_fields, agency_id: data?.id, agency_name: data?.name || "", agency_timezone: data?.agency_timezone || data?.tz || "", errors: { ...prev.ticket_fields.errors, agency_id: undefined } } } : prev;return updated;});
                        }}
                        onInitialLoad={(opt: any) => {
                          const data = opt?.data;
                          if (data) {
                            setEditState(prev => {
                              if (!prev) return prev;
                              // Only update if agency_timezone is missing
                              if (prev.ticket_fields.agency_timezone) {
                                return prev;
                              }
                              const updated= {
                                ...prev,
                                ticket_fields: {
                                  ...prev.ticket_fields,
                                  agency_name: data?.name || prev.ticket_fields.agency_name,
                                  agency_timezone: data?.agency_timezone || ""
                                }
                              };
                              return updated;
                            });
                          }
                        }} />
                    </div>
                    <ErrorText message={editState.ticket_fields.errors?.agency_id} />
                  </div>
                </div>

                {/* Line Items - Existing items from DB */}
                {editState.line_items
                  .filter(li => !li.isNew)
                  .sort((a, b) => (a.violation_item_id || 0) - (b.violation_item_id || 0))
                  .map((lineItem, index) => {
                  const itemId = lineItem.violation_item_id;
                  return (
                    <div key={itemId} style={{ border: "1px solid #e0e0e0", borderRadius: 10, padding: 14, marginBottom: 18, background: "#fff" }}>
                      <Typography variant="subtitle2" style={{ marginBottom: 10, fontWeight: 600 }}>Item {index + 1}</Typography>
                      <div className={classes.lineItemGrid}>
                        {lineItemFields.map(([lbl, k]) => renderLineItemField(itemId, lineItem, lbl, k))}
                      </div>
                    </div>
                  );
                })}

                {/* Line Items - New items (not yet in DB, or verified new items) */}
                {editState.line_items.filter(li => li.isNew).map((lineItem, index) => {
                  const existingCount = editState.line_items.filter(li => !li.isNew).length;
                  return (
                    <div key={lineItem.temp_id} style={{ border: isVerified ? "1px solid #e0e0e0" : "1px solid #1976d2", borderRadius: 10, padding: 14, marginBottom: 18, background: isVerified ? "#fff" : "#f5f9ff" }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 600, color: isVerified ? 'inherit' : '#1976d2' }}>{isVerified ? `Item ${existingCount + index + 1}` : `New Item ${existingCount + index + 1}`}</Typography>
                        {!isVerified && (
                          <IconButton size="small" onClick={() => removeNewLineItem(lineItem.temp_id!)} style={{ color: '#c62828' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                      <div className={classes.lineItemGrid}>
                        {(isVerified ? lineItemFields : lineItemFields.filter(([, k]) => k !== 'status')).map(([lbl, k]) => renderLineItemField(lineItem.temp_id, lineItem, lbl, k))}
                      </div>
                    </div>
                  );
                })}

                {/* Add New Line Item Button */}
                {!isVerified && !isReadOnly && (
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    size="small" 
                    startIcon={<AddIcon />}
                    onClick={addNewLineItem}
                    style={{ marginBottom: 18 }}
                  >
                    Add Line Item
                  </Button>
                )}
                <div className={classes.fieldRow}>
                  {/* Notes - editable in payment phase */}
                  <div className={classes.fieldGroup}>
                    <Typography variant="caption" color="textSecondary" className={classes.fieldLabel}>
                      Notes {isVerified && <span style={{ color: '#d32f2f' }}>*</span>}
                    </Typography>
                    <div className={`${classes.inputFrame}`} style={{height:'maxContent'}}>
                      <textarea
                        value={editState?.ticket_fields.notes ?? ''}
                        disabled={!isVerified}
                        placeholder={isVerified ? "Enter notes before marking as paid" : ""}
                        onChange={(e) => updateTicketField('notes', e.target.value)}
                        style={{
                          width: '100%',
                          height:'28px',
                          fontSize: 14,
                          fontWeight: 500,
                          fontFamily: 'inherit',
                          border: 'none',
                          outline: 'none',
                          background: 'transparent',
                          resize: 'vertical',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                  {/* Created At (read-only) */}
                  <div className={classes.fieldGroup}>
                    <Typography variant="caption" color="textSecondary" className={classes.fieldLabel}>Created At</Typography>
                    <div className={`${classes.inputFrame}`}>
                      <TextField value={selectedViolation.ticket_created_at ? moment(selectedViolation.ticket_created_at).local().format('YYYY-MM-DD HH:mm') : '-'} variant="standard" size="small" fullWidth disabled={true} InputProps={{ style: baseInputStyle, classes: inputClasses }} />
                    </div>
                  </div>
                </div>
                {/* Actions */}
                {!isReadOnly && (
                <div style={{ marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Button variant="contained" color="primary" size="small" onClick={handleVerify} disabled={isVerified || isVerifying || isSubmittingMarkPaid}>{isVerified ? '✓ Verified' : 'Verify'}</Button>
                  <Button variant="contained" size="small" style={{ backgroundColor: isVerified ? '#4caf50' : '#ccc', color: '#fff' }} disabled={!isVerified || isVerifying || isSubmittingMarkPaid} onClick={handleMarkPaidClick}>Mark Paid</Button>
                  {isVerified && <Typography variant="caption" style={{ color: '#2e7d32', marginLeft: 8, fontStyle: 'italic' }}>You can now edit amounts/fees before marking as paid</Typography>}
                </div>
                )}
              </>)}
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <ConfirmationModal open={showMarkPaidConfirmation} title="Mark Payment as Paid" message="Are you sure you want to mark this violation as paid? Please verify the amounts, fees, and notes before confirming." confirmButtonText="Yes, Mark as Paid" cancelButtonText="Cancel" onConfirm={handleMarkPaidConfirm} onCancel={() => setShowMarkPaidConfirmation(false)} showWarningIcon={false} confirmButtonColor="#4caf50" isLoading={isSubmittingMarkPaid} />
      <ConfirmationModal open={!!violationStatusChange} title={`Mark as ${violationStatuses[violationStatusChange || ''] || violationStatusChange}`} message={`Are you sure you want to mark this ticket as ${(violationStatuses[violationStatusChange || ''] || violationStatusChange || '').toLowerCase()}? This action will update the ticket status without requiring verification.`} confirmButtonText={`Yes, Mark as ${violationStatuses[violationStatusChange || ''] || violationStatusChange}`} cancelButtonText="Cancel" onConfirm={handleStatusChangeConfirm} onCancel={() => setViolationStatusChange(null)} showWarningIcon={true} confirmButtonColor={violationStatusChange === 'manually_solved' ? '#ff9800' : '#c62828'} isLoading={isVoiding} />
    </Paper>
  );
};

export default ViolationPage;